import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import {
  formatBattery,
  formatClockTime,
  formatDriverName,
} from '../../../utils/displayFormat';

// The inspection card pinned to the recorded trail point under the cursor: a
// faithful readout of that one DriverLocation reading — the driver whose
// phone reported it, the second it was taken, the route it was stamped with
// and the battery at that moment. On a shared route the reading's driver and
// route stamp can differ from the route's summary, which is the point.
// Self-styled (like StopPopupContent) because Leaflet renders tooltip content
// outside the panes' styled subtrees.

const StyledDiv = styledComponent('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing.gapXs,
  fontFamily: theme.fonts.system,

  '& .thc-name': {
    fontSize: theme.sizes.smallFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .thc-line': {
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textMuted,
  },
  '& .thc-time': {
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textBody,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .thc-battery': {
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textMuted,
    fontVariantNumeric: 'tabular-nums',
  },
});

const NO_ROUTE_ON_READING = 'No route on this reading';

function TrailHoverCard({ driver, point }) {
  // The reading's own email names the driver; the route record only
  // backstops a reading that carried none.
  const email = point.driverEmail || driver.driverEmail;
  const name = point.driverEmail
    ? formatDriverName(point.driverEmail)
    : driver.driverName;
  const battery = formatBattery(point.batteryLevel);
  return (
    <StyledDiv className="thc-card">
      <span className="thc-name">{name || 'Unknown driver'}</span>
      {email ? <span className="thc-line">{email}</span> : null}
      <span className="thc-time">
        {formatClockTime(new Date(point.timestampMs), {
          seconds: true,
          zone: true,
        })}
      </span>
      <span className="thc-line">{point.route || NO_ROUTE_ON_READING}</span>
      {battery ? (
        <span className="thc-battery">{`Battery ${battery}`}</span>
      ) : null}
    </StyledDiv>
  );
}

TrailHoverCard.propTypes = {
  // Fallback identity only, for a reading that carried no driverEmail.
  driver: PropTypes.shape({
    driverName: PropTypes.string,
    driverEmail: PropTypes.string,
  }).isRequired,
  point: PropTypes.shape({
    timestampMs: PropTypes.number.isRequired,
    driverEmail: PropTypes.string,
    route: PropTypes.string,
    batteryLevel: PropTypes.number,
  }).isRequired,
};

export default TrailHoverCard;
